/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import useResponsive from '../../hooks/useResponsive';
import onboardingData from '../../data/mockOnboard.json';
import JobCard from '../../components/JobCard';
import { useNavigate } from 'react-router-dom';
import { useCamera } from '../../hooks/useCamera';
import useRecording from '../../hooks/useRecording';
import { saveTake, getTakesForQuestion, deleteTakesForQuestion, sweepExpiredTakes } from '../../utils/recordingStorage';
import { submitTake, SubmitStatus as SubmitStatusValue } from '../../utils/submitTake';
import SubmitStatus from '../../components/SubmitStatus';
import { getWithExpiry, setWithExpiry, removeItem } from '../../utils/sessionStorage';
import Spinner from '../../components/Spinner';
import ToastMessage, { ToastMessageProps } from '../../components/ToastMessage';
import { useInternetConnection } from '../../utils/connection';
import { S3UploadMetadata } from '../../types/file';
import { useLayout } from '../../context/LayoutContext';
import { isUserFirefox } from '../../utils/firefoxUtils';
import { isUserSafari } from '../../utils/safariUtils';
import { useBlur } from '../../utils/useBlur';
import { useBackgroundBlur, preloadBlurEngine } from '../../hooks/useBackgroundBlur';
import {
  PERMISSION_REQUEST_MESSAGES,
  PERMISSION_BLOCKED_MESSAGES,
  DEVICE_DISCONNECTED_MESSAGES,
  GENERIC_ERROR_MESSAGES,
  getPermissionErrorMessage,
} from '../../constants/permissionMessages';
import { pushScreenerStage, pushPermissionResult, triggerHiddenGtmClick, pushExitIntent, pushTakeSelected } from '../../utils/analytics';
import useExitIntentTrigger from '../../hooks/useExitIntentTrigger';
import ExitIntentModal from '../../components/ExitIntentModal';
import { markInteractedThisSession } from '../../utils/exitIntentStorage';
import { scheduleReminder } from '../../api/ReminderService';
import { EXIT_INTENT_INTERVIEW_TIMINGS } from '../../utils/exitIntentConfig';
import {
  logScreenerRecordingRetaken,
  logScreenerCompleted,
  logScreenerPermission,
  logInterviewTake1Selected,
  logInterviewTake2Selected,
  logInterviewTake3Selected,
  SCREENER_STARTED_AT_KEY,
} from '../../utils/statsig';
import { useExperiment } from '@statsig/react-bindings';
import { TakeRecord } from '../../types/interview';
import CountdownOverlay from '../../components/CountdownOverlay';
import { AnswerTimerBadge, TimerToggle } from '../../components/InterviewTimer';
import PickYourAnswer from '../../components/PickYourAnswer';
import VideoPlayback from '../../components/VideoPlayback';
import { getDeviceType } from '../../utils/getDeviceType';
import { useScreenerAbandonTracker } from '../../hooks/useScreenerAbandonTracker';
import JobDetailsModal from '../../components/JobDetailsModal';
import { isNewInterviewPageEnabled } from '../../utils/featureFlags';

const MAX_RECORDING_TRIES = 3;
// VI-368 (resume): how long restored takes live in IndexedDB. Matches the 24h
// session-storage TTL used for questionnaires/applicantGuid, so a returning
// jobseeker within the same interview session sees their takes; older abandoned
// interviews are swept on mount.
const TAKE_TTL_MS = 24 * 60 * 60 * 1000;

/*
  Video Interview
 */
const Interview = () => {
  const isMobile = useResponsive('md');
  // VI-594: when the user arrives via the Continue / Restart Interview
  // modal on the V2 landing page, that page writes `resumeFromIndex` to
  // sessionStorage so we initialize the recording flow at the right
  // question. Reading via getWithExpiry returns null when the key is
  // absent (fresh start / direct nav) so we fall back to 0.
  const initialQuestionIndex = (getWithExpiry('resumeFromIndex') as number | null) ?? 0;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(initialQuestionIndex);
  // VI-594: resumeFromIndex is a one-shot pointer. Clearing it on mount
  // means a page refresh after the initial jump falls back to question 0
  // (existing pre-VI-594 behavior). Fully cross-refresh resume would
  // require fetching upstream answers on Interview mount too; that's a
  // separate enhancement.
  useEffect(() => {
    removeItem('resumeFromIndex');
  }, []);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [remainingTries, setRemainingTries] = useState(3);
  const [job, setJob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const applicantGuid = getWithExpiry('applicantGuid') || undefined;
  const jobId = getWithExpiry('jobId') || undefined;
  const interviewId = getWithExpiry('interviewId') || undefined;
  const companyId = getWithExpiry('companyId') || undefined;
  const ttsAudio = getWithExpiry('ttsAudio') || undefined;
  const isTTSPlayedRef = useRef(false);
  const navigate = useNavigate();
  const [startTime, setStartTime] = useState<number | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isInterviewCompleted, setIsInterviewCompleted] = useState(false);
  // VI-370: optional answer-timer visualization. Off by default; persisted so a
  // mid-interview refresh keeps the jobseeker's choice (same 24h TTL as takes).
  const [showTimer, setShowTimer] = useState<boolean>(() => getWithExpiry('showAnswerTimer') === true);
  const toggleTimer = () => {
    setShowTimer(prev => {
      const next = !prev;
      setWithExpiry('showAnswerTimer', next, TAKE_TTL_MS);
      return next;
    });
  };
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasStoppedRef = useRef(false); // Prevent multiple stops
  const questionAudioRef = useRef<HTMLAudioElement | null>(null);
  const questionnaires = getWithExpiry('questionnaires');
  const currentQuestion = questionnaires[currentQuestionIndex];
  const totalQuestions = questionnaires.length;
  // Re-enabled - Track selected devices from session storage to detect disconnections
  const selectedCameraDeviceId = getWithExpiry('selectedCameraDeviceId') || undefined;
  const selectedAudioDeviceId = getWithExpiry('selectedAudioDeviceId') || undefined;
  const [isStartingRecording, setIsStartingRecording] = useState(false);

  // VI-368 Phase 1: countdown before recording starts (all users)
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(3);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // VI-702: the pre-warm promise is held in a ref (not a local in startCountdown)
  // so a mid-countdown blur toggle can REPLACE it — the countdown=0 handler then
  // awaits whichever prepare ran last, guaranteeing the recorded stream reflects
  // the jobseeker's final blur choice. preparedBlurRef tracks the blur value the
  // current pre-warmed session was built with, so we only re-prepare on a real change.
  const preparePromiseRef = useRef<Promise<boolean> | null>(null);
  const preparedBlurRef = useRef<boolean>(false);

  // VI-368 Phase 2: multi-take selection (Statsig A/B — Test group only)
  const experiment = useExperiment('choosing_final_response_during_video_interview');
  const canChooseFinalResponse =
    import.meta.env.VITE_FORCE_PICK_ANSWER === 'true' ||
    (experiment.get('can_choose_final_response', false) as boolean);
  const [takes, setTakes] = useState<TakeRecord[]>([]);
  const [selectedTakeIndex, setSelectedTakeIndex] = useState(-1);

  // Track permission states for camera and microphone
  const [permissionsGranted, setPermissionsGranted] = useState({
    camera: false,
    microphone: false
  });

  const s3MetaData: S3UploadMetadata = {
    interviewId: interviewId || '',
    dateInterviewed: new Date().toISOString(),
    companyId: companyId || '',
  };

  const playbackVideoRef = useRef<HTMLVideoElement>(null);

  // asnyc loading state for buttons
  // VI-900: progress of the in-flight submit, shown under the Submit button.
  const [submitStatus, setSubmitStatus] = useState<SubmitStatusValue | null>(null);
  const [submitStartedAt, setSubmitStartedAt] = useState<number | null>(null);
  const [processingButtons, setProcessingButtons] = useState<{ [key: string]: boolean }>({
    start: false,
    stop: false,
    reRecord: false,
    submit: false,

  });

  // toast message
  // UPDATED - No longer showing toast based on pre-selected device
  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessageProps>({
    hasTitle: true,
    title: '',
    message: '',
    type: 'info',
    isMobile: isMobile,
  });

  const isConnected = useInternetConnection();

  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const exitIntentSuspended =
    isLoading ||
    !isConnected ||
    // VI-592: only show the interview modal after the first answer is saved.
    // currentQuestionIndex only advances past 0 once an answer is submitted
    // (and resumed sessions start at the saved count), so < 1 means nothing
    // has been saved yet.
    currentQuestionIndex < 1 ||
    // VI-900: never mid-submit — the modal claims responses were saved.
    processingButtons.submit;

  const {
    isOpen: showExitIntentModal,
    triggerSource: exitIntentTriggerSource,
    dismiss: dismissExitIntent,
  } = useExitIntentTrigger({
    page: 'interview',
    applicantGuid: applicantGuid || '',
    mouseLeaveDelayMs: EXIT_INTENT_INTERVIEW_TIMINGS.mouseLeaveDelayMs,
    idleSeconds: EXIT_INTENT_INTERVIEW_TIMINGS.idleSeconds,
    suspended: exitIntentSuspended,
  });

  const hasReportedExitIntentShown = useRef(false);
  useEffect(() => {
    if (showExitIntentModal && exitIntentTriggerSource && !hasReportedExitIntentShown.current) {
      hasReportedExitIntentShown.current = true;
      pushExitIntent({
        page: 'interview',
        trigger: exitIntentTriggerSource,
        action: 'shown',
        applicant_guid: applicantGuid || '',
        job_id: jobId || '',
      });
    }
    if (!showExitIntentModal) {
      hasReportedExitIntentShown.current = false;
    }
  }, [showExitIntentModal, exitIntentTriggerSource, applicantGuid, jobId]);

  const handleExitIntentContinueInterview = () => {
    pushExitIntent({
      page: 'interview',
      trigger: exitIntentTriggerSource ?? 'idle',
      action: 'continue_interview',
      applicant_guid: applicantGuid || '',
      job_id: jobId || '',
    });
    markInteractedThisSession(applicantGuid || '', 'interview');
    dismissExitIntent();
  };

  const handleExitIntentRemindMeLater = () => {
    const trigger = exitIntentTriggerSource ?? 'idle';
    pushExitIntent({
      page: 'interview',
      trigger,
      action: 'remind_me_later',
      applicant_guid: applicantGuid || '',
      job_id: jobId || '',
    });
    markInteractedThisSession(applicantGuid || '', 'interview');
    if (applicantGuid) {
      scheduleReminder({
        applicantGuid,
        page: 'interview',
        trigger,
      }).catch((error: unknown) => {
        console.error('[VI-548] scheduleReminder failed', error);
      });
    }
    dismissExitIntent();
  };

  useEffect(() => {
    if (!isConnected) {
      const errorConfig = GENERIC_ERROR_MESSAGES.connection_lost;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: errorConfig.message,
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    } else {
      // VI-431: only dismiss the offline notice, never e.g. a recording-failed toast.
      if (toast.title === GENERIC_ERROR_MESSAGES.connection_lost.title) setShowToast(false);
      setError(null);
    }
  }, [isConnected]);

  // general error handling
  useEffect(() => {
    if (error) {
      setToast({
        hasTitle: true,
        title: 'Error',
        message: error,
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);
    }
  }, [error]);

  const handleButtonClick = async (buttonId: string, action: () => Promise<void>) => {
    const startTime = new Date();
    try {
      console.log(buttonId, ' Starting button action', new Date().toISOString());
      setProcessingButtons(prev => ({ ...prev, [buttonId]: true }));
      console.log(buttonId, ' Button action started', new Date().toISOString());
      await action();
      console.log(buttonId, ' Button action completed', new Date().toISOString());
    } finally {
      console.log("Time taken:", new Date().getTime() - startTime.getTime(), "ms");
      setProcessingButtons(prev => ({ ...prev, [buttonId]: false }));
    }
  };


  // Cleanup countdown timer on unmount
  useEffect(() => {
    return () => { if (countdownTimerRef.current) clearInterval(countdownTimerRef.current); };
  }, []);

  const {
    isRecording,
    recordedUrl,
    stopRecording,
    clearRecording,
    setIsRecording,
    startStreamedRecording,
    prepareStreamedRecording,
    startPreparedRecording,
    cancelPreparedSession,
    stopStreamedRecording,
    recordedBlob,
    audioRecordingError,
    error: useRecordingError,
    clearRecordingError,
    takeFailure,
    getActiveSocket,
    isFinalizingTake,
  } = useRecording();

  useEffect(() => {
    if (isRecording && isStartingRecording) {
      setIsStartingRecording(false);
    }
    // VI-368: clear the countdown overlay the moment recording actually begins
    if (isRecording && isCountingDown) {
      setIsCountingDown(false);
      setCountdownValue(3);
    }
  }, [isRecording, isStartingRecording]);

  // VI-368: if a toast fires while we're in the countdown/preparation phase,
  // clear the overlay so the user can see the error.
  useEffect(() => {
    if (showToast && isCountingDown && !isRecording) {
      setIsCountingDown(false);
      setCountdownValue(3);
    }
  }, [showToast]);

  // camera hook
  const {
    isCameraOn,
    videoRef,
    startCamera,
    stopCamera,
    error: useCameraError,
    deviceRemoved,
    deviceReconnected,
    setDeviceRemoved,
    setDeviceReconnected,
    checkSelectedDeviceAvailable: checkCameraDeviceAvailable,
    stream: cameraPreviewStream,
  } = useCamera({ clearRecordingError, hasRecorded });

  // Handle camera device removal errors
  //  useEffect(() => {
  //   if (deviceRemoved) {
  //     console.log('Device removed during recording');
  //     let message = `Your device has been disconnected. Please reconnect your device and try again. (${Date.now()})`;
  //     setToast({
  //       hasTitle: true,
  //       title: 'Device Disconnected',
  //       message: message.replace(/\(\d+\)$/, ''),
  //       type: 'error',
  //       isMobile: isMobile,
  //       isOpen: true,
  //     });
  //     setShowToast(true);

  //     // If recording was in progress, stop it
  //     if (isRecording) {
  //       setStopRecording();
  //     }
  //   }
  //   else{
  //     setDeviceRemoved(false);
  //   }
  // }, [deviceRemoved]);

  // VI-431: a recording attempt failed (connection drop, recorder or device
  // error, connect timeout, server error). Tell the candidate and put them back
  // where they can re-record. The failed attempt is never kept as a take or made
  // submittable, and it doesn't consume a try — the Stop path that decrements
  // tries (setStopRecording) never runs for it.
  useEffect(() => {
    if (!takeFailure) return;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    preparePromiseRef.current = null;
    setIsCountingDown(false);
    setCountdownValue(3);
    setIsStartingRecording(false);
    setRecordingTime(0);
    // Multi-take keeps any earlier good takes (and their selection) to choose
    // from; otherwise go back to the Start state — a re-record is required.
    if (!(canChooseFinalResponse && takes.length > 0)) {
      clearRecording();
      setHasRecorded(false);
      if (!isCameraOn) startCamera(selectedCameraDeviceId || undefined);
    } else {
      // Back to reviewing the earlier takes — same as after a normal Stop.
      stopCamera();
    }
    setToast({
      hasTitle: true,
      title: GENERIC_ERROR_MESSAGES.recording_failed.title,
      message: takeFailure.message,
      type: 'error',
      isMobile: isMobile,
      isOpen: true,
    });
    setShowToast(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeFailure]);

  const handleToastClose = () => {
    setShowToast(false);
    setError(null);
  };

  useEffect(() => {
    if (useRecordingError) {
      const errorConfig = GENERIC_ERROR_MESSAGES.recording_error;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: useRecordingError,
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    }
  }, [useRecordingError]);

  useEffect(() => {
    // Don't update toast if deviceRemovedDuringRecording is already handling the error
    // This prevents camera error from overwriting the actual disconnected device error
    // (e.g., when microphone is disconnected, recording stops, which stops video track too)
    if (useCameraError && !takeFailure) {
      const errorConfig = GENERIC_ERROR_MESSAGES.general_device_error;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: useCameraError || errorConfig.message,
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      //setShowToast(true);
    }
  }, [useCameraError, takeFailure]);

  // audio recording error handling
  useEffect(() => {
    if (audioRecordingError) {
      const errorConfig = GENERIC_ERROR_MESSAGES.audio_silence;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: audioRecordingError.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);
    } else if (toast.title === GENERIC_ERROR_MESSAGES.audio_silence.title) {
      // VI-431: only dismiss the silence warning, never e.g. a recording-failed toast.
      setShowToast(false);
    }
  }, [audioRecordingError]);



  // useEffect(() => {
  //   console.log("Effect: deviceReconnected", deviceReconnected);
  //   if (deviceReconnected && !isCameraOn) {
  //     // Try to turn the camera back on
  //     console.log("Effect: auto-restarting camera")
  //     startCamera(selectedCameraDeviceId || undefined);
  //     setDeviceReconnected(false); // Reset the flag
  //   }
  // }, [deviceReconnected, isCameraOn, selectedCameraDeviceId, startCamera, setDeviceReconnected]);

  // on mount, check if applicationId and jobId are set
  useEffect(() => {
    setJob(getWithExpiry('job'));
  }, [jobId, navigate]);

  // VI-368: set when takes are restored on mount, so initializeMediaPermissions
  // skips auto-starting the live camera (we want to show the restored takes).
  const skipMountCameraRef = useRef(false);

  const hasPushedStage = useRef(false);
  useEffect(() => {
    if (!hasPushedStage.current) {
      hasPushedStage.current = true;
      pushScreenerStage({
        stage_name: 'first_question',
        job_id: jobId || '',
        applicant_guid: applicantGuid || '',
        screener_question_count: totalQuestions,
      });
    }
  }, []);

  // Initialize camera and microphone permissions when component mounts
  // Request both permissions upfront so user doesn't get prompted during recording
  useEffect(() => {
    const initializeMediaPermissions = async () => {
      try {
        console.log('🎥 Interview: Requesting camera and microphone permissions on mount');
        
        if (isUserFirefox()) {
          console.warn("Waiting for Firefox to initialize...");
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        // Clear stale device IDs from previous sessions/permission cycles
        // Browsers (Chrome/Edge) may regenerate device IDs after permission toggle
        removeItem('selectedCameraDeviceId');
        removeItem('selectedAudioDeviceId');
        console.log('🧹 Cleared stale device IDs from session storage');

        // GA: Check permission state before requesting camera
        let permissionStateBeforeRequest: PermissionState | null = null;
        try {
          const permStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
          permissionStateBeforeRequest = permStatus.state;
          if (permStatus.state === 'prompt') {
            pushPermissionResult({ permission_granted: 'deferred', applicant_guid: applicantGuid || '' });
            triggerHiddenGtmClick('btn-askpermission-click');
            logScreenerPermission({ result: 'prompt', permissionType: 'camera', applicantGuid: applicantGuid || '' });
          } else if (permStatus.state === 'denied') {
            pushPermissionResult({ permission_granted: false, applicant_guid: applicantGuid || '' });
            triggerHiddenGtmClick('btn-block-click');
            logScreenerPermission({ result: 'block', permissionType: 'camera', applicantGuid: applicantGuid || '' });
          }
        } catch {
          // permissions.query may not be supported in all browsers
        }

        // Step 1: Request camera permission first
        let cameraGranted = false;
        try {
          console.log('📷 Requesting camera permission...');
          const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });

          // Capture and store the fresh camera device ID
          const videoTrack = cameraStream.getVideoTracks()[0];
          if (videoTrack) {
            const settings = videoTrack.getSettings();
            if (settings.deviceId) {
              console.log('📷 Storing fresh camera device ID:', settings.deviceId);
              setWithExpiry('selectedCameraDeviceId', settings.deviceId, 24 * 60 * 60 * 1000);
            }
          }

          // Stop the permission check stream immediately - we just want the permission
          cameraStream.getTracks().forEach(track => {
            track.stop();
            console.log(`Stopped ${track.kind} track from camera permission check`);
          });
          cameraGranted = true;
          console.log('✅ Camera permission granted');

          // GA: Track permission granted (only fire if state was 'prompt' or unknown to avoid duplicate)
          if (permissionStateBeforeRequest !== 'granted') {
            pushPermissionResult({ permission_granted: true, applicant_guid: applicantGuid || '' });
            triggerHiddenGtmClick('btn-allow-click');
            logScreenerPermission({ result: 'allow', permissionType: 'camera', applicantGuid: applicantGuid || '' });
          }
        } catch (cameraError: any) {
          console.error('❌ Camera permission denied:', cameraError);

          // GA: Track permission denied (only if we haven't already tracked from 'denied' state)
          if (permissionStateBeforeRequest !== 'denied') {
            pushPermissionResult({ permission_granted: false, applicant_guid: applicantGuid || '' });
            triggerHiddenGtmClick('btn-block-click');
            logScreenerPermission({ result: 'block', permissionType: 'camera', applicantGuid: applicantGuid || '' });
          }

          const errorConfig = getPermissionErrorMessage(cameraError, 'camera');
          setToast({
            hasTitle: true,
            title: errorConfig.title,
            message: errorConfig.message,
            type: 'error',
            isMobile: isMobile,
            isOpen: true,
          });
          setShowToast(true);
        }
        
        // Step 2: Request microphone permission after camera
        let microphoneGranted = false;
        try {
          console.log('🎤 Requesting microphone permission...');
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          
          // Capture and store the fresh microphone device ID
          const audioTrack = micStream.getAudioTracks()[0];
          if (audioTrack) {
            const settings = audioTrack.getSettings();
            if (settings.deviceId) {
              console.log('🎤 Storing fresh microphone device ID:', settings.deviceId);
              setWithExpiry('selectedAudioDeviceId', settings.deviceId, 24 * 60 * 60 * 1000);
            }
          }
          
          // Stop the permission check stream immediately - we just want the permission
          micStream.getTracks().forEach(track => {
            track.stop();
            console.log(`Stopped ${track.kind} track from microphone permission check`);
          });
          microphoneGranted = true;
          console.log('✅ Microphone permission granted');
        } catch (micError: any) {
          console.error('❌ Microphone permission denied:', micError);
          const errorConfig = getPermissionErrorMessage(micError, 'microphone');
          setToast({
            hasTitle: true,
            title: errorConfig.title,
            message: errorConfig.message,
            type: 'error',
            isMobile: isMobile,
            isOpen: true,
          });
          setShowToast(true);
        }
        
        // Update permission states
        setPermissionsGranted({
          camera: cameraGranted,
          microphone: microphoneGranted
        });
        
        // Step 3: If camera permission was granted, start the camera preview
        // VI-368: don't start the live preview if we're restoring takes for this
        // question (review mode) — the user should see their recordings, not the
        // camera. Rerecord/advance will start the camera when actually needed.
        if (cameraGranted && !isCameraOn && !skipMountCameraRef.current) {
          console.log('🎥 Starting camera preview...');
          await startCamera(selectedCameraDeviceId || undefined);
          console.log('✅ Camera preview started');
        }
        
      } catch (error) {
        console.error('❌ Interview: Failed to initialize media permissions:', error);
      }
    };

    initializeMediaPermissions();
  }, []); // Empty dependency array - runs once on mount

  // COMMENTED OUT - No longer automatically initializing camera on question change
  // Camera is initialized once by user click
  // useEffect(() => {
  //   const initializeCamera = async () => {
  //     try {
  //       console.log('🎥 Interview: Initializing camera on mount/question change');
  //       if (!isCameraOn) {
  //         if (isUserFirefox()) {
  //           console.warn("Waiting for Firefox to initialize...");
  //           await new Promise(resolve => setTimeout(resolve, 500));
  //         }
  //         console.log('🎥 Interview: Calling startCamera with deviceId:', selectedCameraDeviceId);
  //         const stream = await startCamera(selectedCameraDeviceId || undefined);
  //         if (stream) {
  //           console.log('✅ Interview: Camera initialized successfully');
  //           setNeedsCameraPermission(false);
  //         } else {
  //           console.error('❌ Interview: Camera initialization returned null');
  //         }
  //       } else {
  //         console.log('ℹ️ Interview: Camera already on, skipping initialization');
  //       }
  //     } catch (error) {
  //       console.error('❌ Interview: Failed to initialize camera:', error);
  //     }
  //   }

  //   initializeCamera();
  // }, [currentQuestionIndex, isCameraOn, startCamera]);

  useEffect(() => {
    isTTSPlayedRef.current = false;
  }, [currentQuestionIndex]);

  // Add this useEffect to play TTS audio when question changes
  useEffect(() => {
    if (isTTSPlayedRef.current) return;
    // Clean up previous audio if exists
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.src = '';
    }

    if (ttsAudio && Array.isArray(ttsAudio) && ttsAudio.length > currentQuestionIndex) {
      // Create a URL that the browser can access by converting file paths to API endpoints
      const audioPath = ttsAudio[currentQuestionIndex];
      // Extract just the filename from the path
      const filename = audioPath.split('/').pop() || audioPath.split('\\').pop();

      // Create a proper audio URL - this must match your server route exactly
      const audioUrl = `/api/v1/media/audio/stream/${encodeURIComponent(filename)}`;

      console.log(`Playing TTS audio for question ${currentQuestionIndex} from: ${audioUrl}`);

      // Create a new audio element with the HTTP URL
      const audioElement = new Audio(audioUrl);
      questionAudioRef.current = audioElement;

      // Play the audio automatically
      audioElement.play().catch(error => {
        console.error('Failed to play TTS audio:', error);
      });

      // Add event listener for debugging
      audioElement.addEventListener('ended', () => {
        console.log('TTS audio playback finished');
      });
    }
    else {
      console.log("No TTS audio found or invalid index");
    }

    // Cleanup function
    return () => {
      if (questionAudioRef.current) {
        questionAudioRef.current.pause();
        questionAudioRef.current.src = '';
      }
    };
  }, [currentQuestionIndex]);


  // Handle timer
  useEffect(() => {
    if (isRecording && !startTime) {
      const now = Date.now();
      setStartTime(now);
      // VI-370: reset elapsed at the exact start so the optional timer badge
      // always begins at 2:00 — even if a prior take ended via an error path
      // that flipped isRecording off without the normal stop handler's reset.
      setRecordingTime(0);
      hasStoppedRef.current = false;

      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - now) / 1000);
        setRecordingTime(elapsed);

        // Hard stop at 120 seconds
        if (elapsed >= 120 && !hasStoppedRef.current) {
          hasStoppedRef.current = true;
          console.log('Timer auto-stop triggered at:', elapsed, 'seconds');

          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }

          // Force stop recording immediately
          setStopRecording();
        }
      }, 1000);
    } else if (!isRecording) {
      // Reset when not recording
      setStartTime(null);
      hasStoppedRef.current = false;

      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRecording]);

  // VI-368 (resume): on mount, instead of wiping local recordings, sweep expired
  // ones and restore any takes the jobseeker recorded for THIS question before a
  // refresh/disconnect. The existing landing-page flow already lands them on the
  // correct question index (via answeredCount); this restores the unsubmitted
  // takes within that question so they can still pick/submit/re-record.
  useEffect(() => {
    if (!canChooseFinalResponse || !applicantGuid) return;
    let cancelled = false;
    (async () => {
      try {
        await sweepExpiredTakes(TAKE_TTL_MS);
        const stored = await getTakesForQuestion(applicantGuid, initialQuestionIndex);
        if (cancelled || stored.length === 0) return;
        // Review mode: suppress the mount-time camera start and stop it if it
        // already came up, so the restored takes' playback is visible.
        skipMountCameraRef.current = true;
        stopCamera();
        const restored: TakeRecord[] = stored.map(r => ({
          // ws intentionally absent → submit will use the HTTP re-upload path
          blob: r.blob,
          url: URL.createObjectURL(r.blob),
          thumbnail: r.thumbnail,
          duration: r.duration,
          mediaType: r.mediaType,
          sessionId: r.id,
        }));
        setTakes(restored);
        setSelectedTakeIndex(restored.length - 1);
        setRemainingTries(Math.max(0, MAX_RECORDING_TRIES - restored.length));
        setHasRecorded(true); // show Pick Your Answer + Submit/Rerecord, not the Start screen
      } catch (err) {
        console.warn('[VI-368] Failed to restore takes', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // blur
  const {
    isBlurEnabled,
    isBlurSupported,
    blurError,
    deviceCapabilities,
    engine,
    toggleBlur
  } = useBlur();

  // Add user-friendly error notification
  useEffect(() => {
    if (blurError && isBlurSupported === false) {
      // Show a friendly toast message about blur not being supported
      setToast({
        hasTitle: true,
        title: 'Background Blur Unavailable',
        message: 'Background blur is not supported on this device. You can still record your screener normally.',
        type: 'info', // Use 'info' instead of 'error' to be less alarming
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    }
  }, [blurError, isBlurSupported]);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { hasFrame: blurHasFrame, error: blurRuntimeError } = useBackgroundBlur({
    videoRef,
    canvasRef,
    engine,
    enabled: isBlurEnabled && isCameraOn,
    width: 640,
    height: 480,
  });

  // If the blur engine fails AT RUNTIME (e.g. Mali-G52 GPUs that pass our
  // capability check but still crash inside MediaPipe's GPU postprocessor —
  // VI-507), the hook surfaces an error and the spinner would otherwise stay
  // on forever. Disable blur to kill the spinner, and surface a friendly
  // info-level toast so the user knows recording continues without blur.
  // Distinct from the existing `blurError` (which fires when blur is rejected
  // at *capability check* time, before any frames are attempted).
  useEffect(() => {
    if (blurRuntimeError && isBlurEnabled) {
      console.warn('[Blur] Engine failed at runtime, disabling:', blurRuntimeError);
      toggleBlur();
      setToast({
        hasTitle: true,
        title: 'Background Blur Unavailable',
        message:
          'Background blur could not start on this device. Recording will continue without blur.',
        type: 'info',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    }
  }, [blurRuntimeError, isBlurEnabled, toggleBlur, isMobile]);

  // Only swap the blur canvas on top of the live video once the engine has
  // produced its first fresh frame for this session. Prevents both the black
  // flash on initial blur enable and the frozen-last-frame bug across questions.
  const showBlurCanvas = isBlurEnabled && isCameraOn && blurHasFrame;

  // VI-368: "review mode" — we've recorded (or restored) a take and are not
  // actively recording/counting down, so the preview should show the SELECTED
  // take's playback, not the live camera. Previously this was inferred from
  // !isCameraOn (the normal stop path turns the camera off), but after a
  // refresh the camera is restarted on mount, so we must gate on this explicitly
  // — otherwise the live feed covers the restored takes' playback.
  const isReviewing = hasRecorded && !isRecording && !isCountingDown;
  // VI-431: Submit needs a successful recording to submit — the selected take
  // (multi-take) or the last recording (single-take). This also covers the gap
  // between Stop and the take being delivered by recorder.onstop.
  const hasValidTake = !isFinalizingTake && (canChooseFinalResponse ? takes.length > 0 : !!recordedBlob);
  const canSubmitAnswer = isReviewing && hasValidTake;

  // True while the user has toggled blur ON but the engine hasn't yet produced
  // a frame (MediaPipe WASM/model load + first segmentation pass). Drives a
  // loading spinner on the blur button so the user knows it's working — without
  // this they'd see the button stuck "active" while the live video remains
  // unblurred for a few hundred ms during cold-start.
  const isBlurInitializing = isBlurEnabled && isCameraOn && !blurHasFrame;

  // Mirror blurHasFrame in a ref so async flows (recording start) can poll it
  // without getting a stale closure value. Used to wait until the canvas has a
  // real frame at real camera dims before captureStream() snapshots it.
  const blurHasFrameRef = useRef(false);
  useEffect(() => {
    blurHasFrameRef.current = blurHasFrame;
  }, [blurHasFrame]);

  // VI-702 (A2 preload): as soon as blur is supported and the camera is on,
  // warm the segmentation engine's one-time init (WASM + model + GPU delegate)
  // in the background — WITHOUT running the per-frame loop. This removes the
  // cold-start latency that made turning blur ON mid-countdown spin forever:
  // the model is already resident, so a later toggle only needs to render its
  // first frame. Idempotent (cached singleton) and no steady-state compute.
  useEffect(() => {
    if (isBlurSupported && engine && isCameraOn) {
      preloadBlurEngine(engine);
    }
  }, [isBlurSupported, engine, isCameraOn]);

  // VI-368: callback fired by recorder.onstop (via useRecording) once the blob and
  // thumbnail are ready. Only wired up for the Test group (canChooseFinalResponse).
  const handleTakeReady = ({ ws, blob, url, thumbnail, duration }: {
    ws: WebSocket; blob: Blob; url: string; thumbnail: string; duration: number;
  }) => {
    const takeIndex = takes.length;
    const sessionId = applicantGuid ? `${applicantGuid}_q${currentQuestionIndex}_take${takeIndex}` : undefined;
    const newTake: TakeRecord = { ws, blob, url, thumbnail, duration, mediaType: 'video', sessionId };
    setTakes(prev => [...prev, newTake]);
    setSelectedTakeIndex(takeIndex);

    // VI-368 (resume): persist locally so a refresh/disconnect doesn't lose this
    // take. Best-effort — a storage failure must never break recording.
    if (applicantGuid) {
      saveTake({
        applicantGuid,
        questionIndex: currentQuestionIndex,
        takeIndex,
        mediaType: 'video',
        blob,
        thumbnail,
        duration,
        recordedAt: Date.now(),
        questionText: currentQuestion.questionText,
        s3MetaData,
      }).catch(err => console.warn('[VI-368] saveTake failed', err));
    }
  };

  // VI-368 Phase 1: start the 3-2-1 countdown before recording begins.
  // Pre-warms the media stream + WebSocket in parallel with the countdown so
  // recording starts with zero latency the moment the countdown reaches 0.
  const startCountdown = () => {
    if (!isConnected || useRecordingError) {
      const errorConfig = useRecordingError
        ? GENERIC_ERROR_MESSAGES.recording_error
        : GENERIC_ERROR_MESSAGES.connection_lost;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: useRecordingError ? useRecordingError.replace(/\(\d+\)$/, '') : errorConfig.message,
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
      return;
    }
    if (remainingTries === 0) {
      setError(GENERIC_ERROR_MESSAGES.no_tries_remaining.message);
      return;
    }

    // Mute TTS audio before countdown starts
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.src = '';
    }

    setIsCountingDown(true);
    setCountdownValue(3);

    // Kick off stream + WebSocket setup immediately (runs during the 3 s countdown)
    // VI-702: stored in a ref so a mid-countdown blur toggle can replace it.
    preparedBlurRef.current = isBlurEnabled;
    preparePromiseRef.current = prepareStreamedRecording(
      applicantGuid,
      currentQuestionIndex,
      currentQuestion.questionText,
      currentQuestionIndex === totalQuestions - 1,
      totalQuestions,
      s3MetaData,
      canvasRef,
      isBlurEnabled,
      canChooseFinalResponse ? handleTakeReady : undefined,
    );

    let count = 3;
    countdownTimerRef.current = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdownValue(count);
      } else {
        clearInterval(countdownTimerRef.current!);
        countdownTimerRef.current = null;
        // Wait for prep to finish (usually already done), then start immediately.
        // VI-702: read from the ref so we honour the LATEST prepare — if the user
        // toggled blur mid-countdown, this is the re-prepared (correctly-blurred)
        // session, and awaiting it also covers a re-prepare still settling at t=0.
        preparePromiseRef.current?.then((ready) => {
          if (ready) {
            startPreparedRecording();
          }
          // else: error was surfaced by prepareStreamedRecording; the existing
          // useEffect (showToast && isCountingDown) clears the overlay automatically
        }).catch(() => {
          setIsCountingDown(false);
          setCountdownValue(3);
        });
      }
    }, 1000);
  };

  const cancelCountdown = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    cancelPreparedSession(); // Release the pre-warmed stream + WS
    preparePromiseRef.current = null;
    setIsCountingDown(false);
    setCountdownValue(3);
    // remainingTries is NOT decremented — countdown cancel is free
  };

  // VI-702 (experiment): let the jobseeker flip background blur DURING the
  // 3-2-1 countdown and have the recorded take reflect their final choice.
  // The pre-warmed session bakes blur in at prep time (createCompositeStream ->
  // canvas.captureStream), so a preview-only toggle wouldn't change the output.
  // When blur changes mid-countdown we re-run prepareStreamedRecording (which
  // internally cancels the previous pre-warmed session first) so the new state
  // is baked into the stream that startPreparedRecording() will use at t=0.
  useEffect(() => {
    // Only re-prepare while an active countdown is running (not idle/recording).
    if (!isCountingDown || isRecording) return;
    // Skip the run triggered when the countdown first starts: startCountdown
    // already prepared with this exact blur value.
    if (preparedBlurRef.current === isBlurEnabled) return;

    preparedBlurRef.current = isBlurEnabled;
    // Assign the new promise synchronously so the countdown=0 handler awaits it
    // even if the settle wait below is still in flight.
    preparePromiseRef.current = (async () => {
      // Timing caveat: when turning blur ON we must wait for the MediaPipe engine
      // to render a real frame at the camera's dims before captureStream snapshots
      // the canvas — otherwise the take can start unblurred or resize mid-stream
      // (same guard handleRecordButtonClick uses). If the user toggles blur very
      // late in the countdown, this wait can push recording start slightly past
      // t=0; the countdown=0 handler awaits this promise so it still starts, just
      // marginally later. This is an experiment — product should test late toggles.
      if (isBlurEnabled && canvasRef.current && videoRef.current) {
        let attempts = 0;
        const MAX_ATTEMPTS = 25; // ~2.5s budget
        while (attempts < MAX_ATTEMPTS) {
          const canvas = canvasRef.current;
          const video = videoRef.current;
          const dimsMatch =
            !!canvas &&
            !!video &&
            video.videoWidth > 0 &&
            video.videoHeight > 0 &&
            canvas.width === video.videoWidth &&
            canvas.height === video.videoHeight;
          if (dimsMatch && blurHasFrameRef.current) break;
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
      }
      return prepareStreamedRecording(
        applicantGuid,
        currentQuestionIndex,
        currentQuestion.questionText,
        currentQuestionIndex === totalQuestions - 1,
        totalQuestions,
        s3MetaData,
        canvasRef,
        isBlurEnabled,
        canChooseFinalResponse ? handleTakeReady : undefined,
      );
    })();
  }, [isBlurEnabled, isCountingDown]);

  const handleRerecordClick = () => {
    // VI-431: never start a new attempt while the current answer is being
    // submitted, or before the previous take has been delivered.
    if (processingButtons.submit || isFinalizingTake) return;
    if (!canChooseFinalResponse) {
      // Single-take: the new recording replaces this one, so release its socket
      // (no finalize signal → the server discards it) instead of leaving its
      // FFmpeg process open. Submit falls back to HTTP if the countdown is cancelled.
      const previousSocket = getActiveSocket();
      if (previousSocket && previousSocket.readyState === WebSocket.OPEN) previousSocket.close();
    }
    const attemptNumber = MAX_RECORDING_TRIES - remainingTries + 1;
    logScreenerRecordingRetaken({
      jobId,
      companyId,
      applicantGuid,
      deviceType: getDeviceType(),
      questionNumber: currentQuestionIndex + 1,
      attemptNumber,
    });
    // Restart the camera immediately so the user sees the live feed during
    // the countdown rather than the old recording preview.
    if (!isCameraOn) {
      startCamera(selectedCameraDeviceId || undefined);
    }
    startCountdown();
  };

  // VI-368: fires when user selects a take thumbnail in PickYourAnswer
  const handleTakeSelect = (index: number) => {
    if (processingButtons.submit) return; // VI-431: don't switch takes mid-submit
    setSelectedTakeIndex(index);
    const takeNum = (index + 1) as 1 | 2 | 3;
    const baseProps = { jobId, companyId, applicantGuid, deviceType: getDeviceType() };
    if (takeNum === 1) logInterviewTake1Selected(baseProps);
    else if (takeNum === 2) logInterviewTake2Selected(baseProps);
    else logInterviewTake3Selected(baseProps);
    pushTakeSelected(takeNum, applicantGuid || '', jobId);
  };

  const handleRecordButtonClick = async () => {
    try{
      if(!isConnected || useRecordingError){
        const errorConfig = useRecordingError 
          ? GENERIC_ERROR_MESSAGES.recording_error 
          : GENERIC_ERROR_MESSAGES.connection_lost;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: useRecordingError ? useRecordingError.replace(/\(\d+\)$/, '') : errorConfig.message,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return;
      }
      
      if(remainingTries === 0){
        setError(GENERIC_ERROR_MESSAGES.no_tries_remaining.message);
        return;
      }

      // Step 0: Verify selected devices are still available using getUserMedia
      // This is resilient to stale device IDs (e.g. after permission toggle in Chrome/Edge)
      const storedCameraId = getWithExpiry('selectedCameraDeviceId');
      const storedMicId = selectedAudioDeviceId || getWithExpiry('selectedAudioDeviceId');
      
      // Step 0 only refreshes stale device IDs - all blocking is done by the permission check below
      
      // Verify camera device via getUserMedia (not just enumerateDevices)
      if (storedCameraId) {
        try {
          const testStream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: storedCameraId } } });
          testStream.getTracks().forEach(t => t.stop());
          console.log('📷 Camera device verified:', storedCameraId);
        } catch (camErr: any) {
          if (camErr?.name === 'OverconstrainedError') {
            // Stale ID - try to refresh with default device
            console.warn('📷 Stored camera ID is stale, refreshing...');
            try {
              const freshStream = await navigator.mediaDevices.getUserMedia({ video: true });
              const freshTrack = freshStream.getVideoTracks()[0];
              if (freshTrack?.getSettings().deviceId) {
                setWithExpiry('selectedCameraDeviceId', freshTrack.getSettings().deviceId!, 24 * 60 * 60 * 1000);
                console.log('📷 Camera ID refreshed to:', freshTrack.getSettings().deviceId);
              }
              freshStream.getTracks().forEach(t => t.stop());
            } catch {
              // Refresh also failed - defer to permission check which will show the correct message
              console.log('📷 Camera refresh failed, deferring to permission check');
            }
          } else {
            // NotAllowedError or any other error - defer to permission check step
            // This ensures consistent "blocked" messaging instead of "device not available"
            console.log('📷 Camera device check error, deferring to permission check:', camErr?.name);
          }
        }
      }
      
      // Verify microphone device via getUserMedia
      if (storedMicId) {
        try {
          const testStream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: storedMicId } } });
          testStream.getTracks().forEach(t => t.stop());
          console.log('🎤 Mic device verified:', storedMicId);
        } catch (micErr: any) {
          if (micErr?.name === 'OverconstrainedError') {
            // Stale ID - try to refresh with default device
            console.warn('🎤 Stored mic ID is stale, refreshing...');
            try {
              const freshStream = await navigator.mediaDevices.getUserMedia({ audio: true });
              const freshTrack = freshStream.getAudioTracks()[0];
              if (freshTrack?.getSettings().deviceId) {
                setWithExpiry('selectedAudioDeviceId', freshTrack.getSettings().deviceId!, 24 * 60 * 60 * 1000);
                console.log('🎤 Mic ID refreshed to:', freshTrack.getSettings().deviceId);
              }
              freshStream.getTracks().forEach(t => t.stop());
            } catch {
              // Refresh also failed - defer to permission check which will show the correct message
              console.log('🎤 Mic refresh failed, deferring to permission check');
            }
          } else {
            // NotAllowedError or any other error - defer to permission check step
            // This ensures consistent "blocked" messaging instead of "device not available"
            console.log('🎤 Mic device check error, deferring to permission check:', micErr?.name);
          }
        }
      }
      
      // Always verify permissions before starting recording
      // This ensures we catch cases where user revoked permissions after initial grant
      let cameraPermissionOk = false;
      let micPermissionOk = false;
      
      // Safari-specific: Check if the existing camera preview stream was revoked.
      // Safari doesn't support navigator.permissions.query() and may still return a valid
      // stream from getUserMedia even after the user denies camera in website settings.
      // The most reliable signal is whether the preview stream's tracks are still alive.
      if (isUserSafari() && cameraPreviewStream) {
        const previewVideoTrack = cameraPreviewStream.getVideoTracks()[0];
        if (previewVideoTrack && (previewVideoTrack.readyState !== 'live' || previewVideoTrack.muted)) {
          console.log('📷 Safari: Camera preview track is dead/muted - camera access was revoked');
          const errorConfig = PERMISSION_BLOCKED_MESSAGES.camera;
          setToast({
            hasTitle: true,
            title: errorConfig.title,
            message: errorConfig.message,
            type: 'error',
            isMobile: isMobile,
            isOpen: true,
          });
          setShowToast(true);
          setPermissionsGranted(prev => ({ ...prev, camera: false }));
          return;
        }
      }
      
      // Step 1: Check camera permission state first, show toast if blocked, then request
      try {
        console.log('📷 Checking camera permission state...');
        
        // Check if permissions API is supported for camera
        let cameraPermissionState: PermissionState | null = null;
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          cameraPermissionState = cameraPermission.state;
          console.log('📷 Camera permission state:', cameraPermissionState);
        } catch (permQueryError) {
          console.log('📷 Permissions API not supported for camera, will check via getUserMedia');
        }
        
        // If we know permission is denied, show toast FIRST
        if (cameraPermissionState === 'denied') {
          console.log('📷 Camera permission is blocked, showing toast first...');
          const errorConfig = PERMISSION_BLOCKED_MESSAGES.camera;
          setToast({
            hasTitle: true,
            title: errorConfig.title,
            message: errorConfig.message,
            type: 'error',
            isMobile: isMobile,
            isOpen: true,
          });
          setShowToast(true);
          setPermissionsGranted(prev => ({ ...prev, camera: false }));
          
          // Safari: getUserMedia may auto-succeed from session cache even when denied,
          // bypassing the user's denial without showing a visible prompt. Just return.
          if (isUserSafari()) {
            console.log('📷 Safari: Permission denied, not retrying getUserMedia (session cache bypass)');
            return;
          }
          
          // On other browsers, attempt to re-request permission (triggers visible prompt)
          try {
            console.log('📷 Attempting to re-request camera permission...');
            const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
            cameraStream.getTracks().forEach(track => track.stop());
            cameraPermissionOk = true;
            setPermissionsGranted(prev => ({ ...prev, camera: true }));
            console.log('✅ Camera permission granted after re-request');
          } catch (e) {
            console.error('❌ Camera permission still denied after re-request');
            return; // Don't proceed with recording
          }
        } else {
          // Permission is 'granted', 'prompt', or unknown (permissions API not supported)
          // Try to get access - if it fails with NotAllowedError, show toast first then retry
          try {
            const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
            
            // Validate stream is actually live (Safari may return dead/muted tracks when device is disabled at OS level)
            const camTrack = cameraStream.getVideoTracks()[0];
            if (!camTrack || camTrack.readyState !== 'live' || camTrack.muted) {
              console.log('📷 Camera stream returned dead/muted track (likely disabled at OS level)');
              cameraStream.getTracks().forEach(track => track.stop());
              throw new Error('Camera track is not live');
            }
            
            // Safari may initially report track as live but it can go dead shortly after
            if (isUserSafari()) {
              await new Promise(resolve => setTimeout(resolve, 300));
              if (camTrack.readyState !== 'live' || camTrack.muted) {
                console.log('📷 Camera track became dead/muted after Safari delay check');
                cameraStream.getTracks().forEach(track => track.stop());
                throw new Error('Camera track is not live');
              }
            }
            
            cameraStream.getTracks().forEach(track => track.stop());
            cameraPermissionOk = true;
            setPermissionsGranted(prev => ({ ...prev, camera: true }));
            console.log('✅ Camera permission verified');
          } catch (getUserMediaError: any) {
            if (getUserMediaError?.name === 'NotAllowedError') {
              // Permission was denied - show toast FIRST
              console.log('📷 Camera access denied, showing toast first...');
              const errorConfig = PERMISSION_BLOCKED_MESSAGES.camera;
              setToast({
                hasTitle: true,
                title: errorConfig.title,
                message: errorConfig.message,
                type: 'error',
                isMobile: isMobile,
                isOpen: true,
              });
              setShowToast(true);
              setPermissionsGranted(prev => ({ ...prev, camera: false }));
              
              // Safari: getUserMedia may auto-succeed from session cache. Just return.
              if (isUserSafari()) {
                console.log('📷 Safari: Permission denied, not retrying getUserMedia');
                return;
              }
              
              // On other browsers, attempt to re-request after showing toast
              try {
                console.log('📷 Attempting to re-request camera permission...');
                const retryStream = await navigator.mediaDevices.getUserMedia({ video: true });
                retryStream.getTracks().forEach(track => track.stop());
                cameraPermissionOk = true;
                setPermissionsGranted(prev => ({ ...prev, camera: true }));
                console.log('✅ Camera permission granted after re-request');
              } catch (retryError) {
                console.error('❌ Camera permission still denied after re-request');
                return; // Don't proceed with recording
              }
            } else {
              // Other error (device not found, dead stream, etc.)
              throw getUserMediaError;
            }
          }
        }
      } catch (cameraError: any) {
        console.error('❌ Camera permission check/request failed:', cameraError);
        setPermissionsGranted(prev => ({ ...prev, camera: false }));
        const errorConfig = PERMISSION_BLOCKED_MESSAGES.camera;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: errorConfig.message,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return; // Don't proceed with recording
      }
      
      // Step 2: Check microphone permission state first, show toast if blocked, then request
      try {
        console.log('🎤 Checking microphone permission state...');
        
        // Check if permissions API is supported for microphone
        let micPermissionState: PermissionState | null = null;
        try {
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          micPermissionState = micPermission.state;
          console.log('🎤 Microphone permission state:', micPermissionState);
        } catch (permQueryError) {
          console.log('🎤 Permissions API not supported for microphone, will check via getUserMedia');
        }
        
        // If we know permission is denied, show toast FIRST
        if (micPermissionState === 'denied') {
          console.log('🎤 Microphone permission is blocked, showing toast first...');
          const errorConfig = PERMISSION_BLOCKED_MESSAGES.microphone;
          setToast({
            hasTitle: true,
            title: errorConfig.title,
            message: errorConfig.message,
            type: 'error',
            isMobile: isMobile,
            isOpen: true,
          });
          setShowToast(true);
          setPermissionsGranted(prev => ({ ...prev, microphone: false }));
          
          // Safari: getUserMedia may auto-succeed from session cache even when denied,
          // bypassing the user's denial without showing a visible prompt. Just return.
          if (isUserSafari()) {
            console.log('🎤 Safari: Permission denied, not retrying getUserMedia (session cache bypass)');
            return;
          }
          
          // On other browsers, attempt to re-request permission (triggers visible prompt)
          try {
            console.log('🎤 Attempting to re-request microphone permission...');
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            micStream.getTracks().forEach(track => track.stop());
            micPermissionOk = true;
            setPermissionsGranted(prev => ({ ...prev, microphone: true }));
            console.log('✅ Microphone permission granted after re-request');
          } catch (e) {
            console.error('❌ Microphone permission still denied after re-request');
            return; // Don't proceed with recording
          }
        } else {
          // Permission is 'granted', 'prompt', or unknown (permissions API not supported)
          // Try to get access - if it fails with NotAllowedError, show toast first then retry
          try {
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Validate stream is actually live (Safari may return dead/muted tracks when device is disabled at OS level)
            const micTrack = micStream.getAudioTracks()[0];
            if (!micTrack || micTrack.readyState !== 'live' || micTrack.muted) {
              console.log('🎤 Mic stream returned dead/muted track (likely disabled at OS level)');
              micStream.getTracks().forEach(track => track.stop());
              throw new Error('Microphone track is not live');
            }
            
            // Safari may initially report track as live but it can go dead shortly after
            if (isUserSafari()) {
              await new Promise(resolve => setTimeout(resolve, 300));
              if (micTrack.readyState !== 'live' || micTrack.muted) {
                console.log('🎤 Mic track became dead/muted after Safari delay check');
                micStream.getTracks().forEach(track => track.stop());
                throw new Error('Microphone track is not live');
              }
            }
            
            micStream.getTracks().forEach(track => track.stop());
            micPermissionOk = true;
            setPermissionsGranted(prev => ({ ...prev, microphone: true }));
            console.log('✅ Microphone permission verified');
          } catch (getUserMediaError: any) {
            if (getUserMediaError?.name === 'NotAllowedError') {
              // Permission was denied - show toast FIRST
              console.log('🎤 Microphone access denied, showing toast first...');
              const errorConfig = PERMISSION_BLOCKED_MESSAGES.microphone;
              setToast({
                hasTitle: true,
                title: errorConfig.title,
                message: errorConfig.message,
                type: 'error',
                isMobile: isMobile,
                isOpen: true,
              });
              setShowToast(true);
              setPermissionsGranted(prev => ({ ...prev, microphone: false }));
              
              // Safari: getUserMedia may auto-succeed from session cache. Just return.
              if (isUserSafari()) {
                console.log('🎤 Safari: Permission denied, not retrying getUserMedia');
                return;
              }
              
              // On other browsers, attempt to re-request after showing toast
              try {
                console.log('🎤 Attempting to re-request microphone permission...');
                const retryStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                retryStream.getTracks().forEach(track => track.stop());
                micPermissionOk = true;
                setPermissionsGranted(prev => ({ ...prev, microphone: true }));
                console.log('✅ Microphone permission granted after re-request');
              } catch (retryError) {
                console.error('❌ Microphone permission still denied after re-request');
                return; // Don't proceed with recording
              }
            } else {
              // Other error (device not found, dead stream, etc.)
              throw getUserMediaError;
            }
          }
        }
      } catch (micError: any) {
        console.error('❌ Microphone permission check/request failed:', micError);
        setPermissionsGranted(prev => ({ ...prev, microphone: false }));
        const errorConfig = PERMISSION_BLOCKED_MESSAGES.microphone;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: errorConfig.message,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return; // Don't proceed with recording
      }

      // Only proceed if both permissions are granted
      if (!cameraPermissionOk || !micPermissionOk) {
        return;
      }

      if (!isCameraOn) {
        // Turn on camera first
        await startCamera(selectedCameraDeviceId || undefined);
      }

      if (isRecording) {
        console.log('handleRecordButtonClick: Stopping streamed recording');
        await stopStreamedRecording();
      } else {
        // If blur is enabled, wait until the engine has produced a real frame
        // AT THE CAMERA'S ACTUAL DIMS before letting the recorder snapshot the
        // canvas. captureStream() pulls the canvas's current pixel buffer and
        // any subsequent canvas.width/height change is encoded as a mid-stream
        // resolution change in the recorded video — that's what made S3
        // playback grow/shrink. We block here until both conditions hold so
        // the recorded track has one stable resolution from frame 0.
        if (isBlurEnabled && canvasRef.current && videoRef.current) {
          console.log('🎯 Waiting for blur canvas to settle at camera dims...');

          let attempts = 0;
          const MAX_ATTEMPTS = 25; // ~2.5s budget
          while (attempts < MAX_ATTEMPTS) {
            const canvas = canvasRef.current;
            const video = videoRef.current;
            const dimsMatch =
              !!canvas &&
              !!video &&
              video.videoWidth > 0 &&
              video.videoHeight > 0 &&
              canvas.width === video.videoWidth &&
              canvas.height === video.videoHeight;

            if (dimsMatch && blurHasFrameRef.current) {
              console.log(
                `✅ Blur canvas stable at ${canvas.width}x${canvas.height}; safe to record`
              );
              break;
            }

            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
          }

          if (attempts >= MAX_ATTEMPTS) {
            console.warn('⚠️ Blur canvas did not stabilize in time; proceeding anyway');
          }
        }
        setIsStartingRecording(true);
        const result = await startStreamedRecording(
          applicantGuid,
          currentQuestionIndex,
          currentQuestion.questionText,
          currentQuestionIndex === totalQuestions - 1,
          totalQuestions,
          s3MetaData,
          canvasRef,
          isBlurEnabled,
          canChooseFinalResponse ? handleTakeReady : undefined);

        if (result === null) {
          setIsStartingRecording(false);
        }
      }
    }catch(error){
      console.error('Recording button error:', error);
      setError(GENERIC_ERROR_MESSAGES.recording_error.message);
      setIsStartingRecording(false);
    }
  };  

  const goToNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      // Don't clear recording here - we want to keep it stored
      setHasRecorded(false);
      setRemainingTries(3);
    }
  };

  const formatTimeRemaining = () => {
    const secondsRemaining = 120 - recordingTime;
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Play recorded video
  const playRecording = () => {
    try {
      setError(null);
      if (playbackVideoRef.current && recordedUrl) {
        playbackVideoRef.current.src = recordedUrl;
        // playbackVideoRef.current.style.display = 'block';
        playbackVideoRef.current.muted = false;
        playbackVideoRef.current.volume = 1.0;

        // Play the video with audio
        playbackVideoRef.current.play().catch(e => console.error('Playback error:', e));
      }
    } catch (error) {
      console.error('Error playing recording:', error);
      setError(`Unable to play recording. (${Date.now()})`);
    }

  };

  // Replay just the audio
  const playAudio = () => {
    try {
      setError(null);
      setIsMuted(!isMuted);
      if (playbackVideoRef.current && recordedUrl) {
        playbackVideoRef.current.muted = isMuted;
        playbackVideoRef.current.play().catch(e => console.error('Audio playback error:', e));

        // cleanup
        const onEnded = () => {
          // console.log('Audio playback finished');
          playbackVideoRef.current?.removeEventListener('ended', onEnded);
        };

        playbackVideoRef.current.addEventListener('ended', onEnded);
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setError(`Unable to play audio. (${Date.now()})`);
    }
  };

  const setStopRecording = async () => {
    // await new Promise(resolve => setTimeout(resolve, 2000));
    console.log('setStopRecording: Stopping streamed recording');
    setRecordingTime(0);
    stopStreamedRecording();
    stopRecording();
    setHasRecorded(true);
    setRemainingTries(prev => prev - 1);
    stopCamera();
    // setTimeout(() => {
    //   startCamera(selectedCameraDeviceId || undefined);
    // }, 500);
  };

  // Simplified submitStreamRecording
  const submitStreamRecording = async () => {
    if (!isConnected) {
      const errorConfig = GENERIC_ERROR_MESSAGES.connection_lost;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: errorConfig.message,
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
      return;
    }

    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.src = '';
    }

    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

    // VI-431: resolve the take to submit. The multi-take flow submits the
    // selected take; the single-take flow submits the hook's last recording on
    // its socket. A failed attempt is never a take and clears recordedBlob, so
    // there is nothing to submit after a failure until a successful re-record.
    const finalIndex = canChooseFinalResponse
      ? (selectedTakeIndex >= 0 && selectedTakeIndex < takes.length ? selectedTakeIndex : takes.length - 1)
      : -1;
    const finalTake = canChooseFinalResponse
      ? takes[finalIndex]
      : recordedBlob
        ? { ws: getActiveSocket(), blob: recordedBlob }
        : null;

    if (!finalTake) {
      const errorConfig = GENERIC_ERROR_MESSAGES.recording_failed;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: errorConfig.message,
        type: 'error',
        isMobile,
        isOpen: true,
      });
      setShowToast(true);
      return;
    }

    setSubmitStartedAt(Date.now());
    setSubmitStatus({ phase: 'saving' });
    try {
      // Resolves only once the server has taken ownership of the answer — the ack
      // of process_recording on the live socket (it then finishes saving even if
      // the connection drops), or a successful HTTP re-upload of the local copy
      // (restored take, or the socket dropped during review).
      await submitTake(finalTake, {
        mode: 'video',
        applicantGuid: applicantGuid || '',
        questionId: currentQuestionIndex,
        questionText: currentQuestion.questionText,
        isLastQuestion,
        totalQuestions,
        s3MetaData,
      }, setSubmitStatus);
    } catch (err) {
      console.error('[VI-431] Answer submission failed', err);
      const errorConfig = GENERIC_ERROR_MESSAGES.submit_failed;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: errorConfig.message,
        type: 'error',
        isMobile,
        isOpen: true,
      });
      setShowToast(true);
      return; // stay on this question with takes intact so they can retry
    } finally {
      setSubmitStatus(null);
      setSubmitStartedAt(null);
    }

    if (canChooseFinalResponse) {
      // VI-368: discard the non-selected takes. Closing their sockets without the
      // finalize signal makes the server drop them.
      takes.forEach((take, i) => {
        if (i !== finalIndex && take.ws && take.ws.readyState !== WebSocket.CLOSED) {
          take.ws.close();
        }
      });
      takes.forEach((take, i) => {
        if (i !== finalIndex) URL.revokeObjectURL(take.url);
      });
      if (applicantGuid) {
        deleteTakesForQuestion(applicantGuid, currentQuestionIndex).catch(() => {});
      }
    }

    if (!isLastQuestion) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setHasRecorded(false);
      setRemainingTries(3);
      // Revoke all remaining object URLs before clearing takes
      takes.forEach(take => URL.revokeObjectURL(take.url));
      setTakes([]);
      setSelectedTakeIndex(-1);
      goToNextQuestion();

      if (!isCameraOn) {
        console.log('🎥 Restarting camera for next question...');
        setTimeout(async () => {
          await startCamera(selectedCameraDeviceId || undefined);
        }, 500);
      }
    } else {
      const startedAtRaw = getWithExpiry(SCREENER_STARTED_AT_KEY);
      const startedAt = typeof startedAtRaw === 'number'
        ? startedAtRaw
        : typeof startedAtRaw === 'string'
          ? parseInt(startedAtRaw, 10)
          : null;
      const totalTimeSeconds = startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;

      logScreenerCompleted({
        jobId,
        companyId,
        applicantGuid,
        deviceType: getDeviceType(),
        totalQuestions,
        totalTimeSeconds,
      });

      setIsInterviewCompleted(true);
      navigate('/v1/interview/success');
    }
  };

  useScreenerAbandonTracker({
    lastStep: 'questions',
    questionNumber: currentQuestionIndex + 1,
    isCompleted: isInterviewCompleted,
  });

  // VI-834: view-only state/derivations for the redesigned (flag-on) layout.
  // None of this touches the recording pipeline.
  const newLayout = isNewInterviewPageEnabled();
  const [showJobDetails, setShowJobDetails] = useState(false);
  // VI-834 mobile: the question panel is a bottom sheet — collapsed (truncated)
  // by default, slides up over the controls on tap, X collapses it back.
  const [questionExpanded, setQuestionExpanded] = useState(false);
  const applicantFirstName = (getWithExpiry('applicantFirstName') as string) || '';
  const jobInfo = job as any;
  const companyName = jobInfo?.companyName || '';
  const jobTitle = jobInfo?.title || '';
  const jobLocation = jobInfo?.locations?.[0]
    ? [jobInfo.locations[0].city, jobInfo.locations[0].region, jobInfo.locations[0].country]
        .filter(Boolean)
        .join(', ')
    : '';

  // VI-834: NOTE — the redesigned page intentionally does NOT post IFRAME_HEIGHT.
  // Like the legacy recording pages it fills the host-allocated iframe height
  // (root minHeight:100vh + full-bleed gray); posting scrollHeight shrank the
  // iframe to the content and exposed the host's white background below it.

  // VI-702: blur control shared by the pre-record and recording states so it
  // matches Figma (blur option stays visible once recording starts). It is
  // locked only DURING recording — MediaRecorder can't swap the video track
  // mid-stream, so toggling then would change only the preview, not the output.
  // It is intentionally UNLOCKED during the 3-2-1 countdown (experiment): a
  // countdown-time toggle re-prepares the pre-warmed session (see the re-prepare
  // useEffect) so the recorded take reflects the jobseeker's final blur choice.
  const blurToggleLocked = isRecording;
  // VI-846: shared blur-tooltip copy — native `title` on the legacy pages, a custom
  // hover bubble (matching the Show Timer tooltip) on the redesign.
  const blurTooltipText = blurToggleLocked
    ? 'Background blur can’t be changed while recording'
    : isBlurInitializing
      ? 'Applying Background Blur…'
      : !isBlurEnabled
        ? 'Apply Background Blur'
        : 'Remove Background Blur';
  // VI-846 / AC: the blur toggle must be present throughout the entire interview
  // (incl. the review window between Stop and Re-Record, when the camera is off).
  // The legacy layout keeps the `isCameraOn` gate; the redesign keeps it visible.
  const blurButton = (isBlurSupported && (isCameraOn || newLayout)) ? (
    <div>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <button
          className={`test-button ${isBlurEnabled ? 'active' : ''}`}
          onClick={() => {
            if (blurToggleLocked) return;
            handleButtonClick('blurTest', async () => {
              toggleBlur();
              console.log('✅ Blur toggled');
            });
          }}
          id="btn-blurbackground-click"
          disabled={processingButtons.blurTest || blurToggleLocked}
          aria-busy={isBlurInitializing}
          title={newLayout ? undefined : blurTooltipText}
          style={{
            width: '100%',
            padding: '0px',
            border: 'none',
            borderRadius: '6px',
            cursor: (processingButtons.blurTest || blurToggleLocked || !isBlurSupported) ? 'not-allowed' : 'pointer',
            opacity: blurToggleLocked ? 0.5 : 1,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!processingButtons.blurTest && !isBlurInitializing && !blurToggleLocked) {
              const img = e.currentTarget.querySelector('img');
              if (img) img.style.filter = 'brightness(0.8)';
            }
          }}
          onMouseLeave={(e) => {
            if (!processingButtons.blurTest && !isBlurInitializing && !blurToggleLocked) {
              const img = e.currentTarget.querySelector('img');
              if (img) img.style.filter = isBlurEnabled ? 'brightness(0.8)' : 'none';
            }
          }}
        >
          {(processingButtons.blurTest || isBlurInitializing) ? (
            <div style={{ width: '2.4rem', height: '2.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spinner size="sm" color="primary" />
            </div>
          ) : (
            <img
              src="/svgs/official-blur.svg"
              alt="Blur"
              style={{
                width: '2.2rem',
                height: '2.4rem',
                filter: isBlurEnabled ? 'brightness(0.8)' : 'none',
                transition: 'filter 0.3s ease',
              }}
            />
          )}
        </button>
      </div>
    </div>
  ) : null;

  // VI-846: wrap the blur toggle in a hover-tooltip matching the Show Timer bubble
  // (dark, right-anchored so it stays on-screen at the control-row's far edge).
  // Self-contained `<style>` — must NOT rely on TimerToggle's styles, which are only
  // rendered while the timer is OFF (in "Hide Timer" state it renders no <style>,
  // which previously left this bubble unstyled and broke the layout).
  const blurControl = blurButton ? (
    <span className="vi-blur-tip">
      <style>{`
        .vi-blur-tip { position: relative; display: inline-flex; }
        .vi-blur-tip__bubble {
          position: absolute; bottom: calc(100% + 8px); right: 0; left: auto;
          transform: translateY(4px);
          background: #1f2733; color: #fff; font-size: 12px; line-height: 1.4;
          text-align: center; padding: 6px 10px; border-radius: 8px;
          width: max-content; max-width: 220px; white-space: normal;
          box-shadow: 0 6px 24px rgba(0,0,0,0.28);
          opacity: 0; pointer-events: none;
          transition: opacity .16s ease, transform .16s ease; z-index: 30;
        }
        .vi-blur-tip__bubble::after {
          content: ''; position: absolute; top: 100%; right: 14px;
          border: 6px solid transparent; border-top-color: #1f2733;
        }
        .vi-blur-tip:hover .vi-blur-tip__bubble { opacity: 1; transform: translateY(0); }
        @media (prefers-reduced-motion: reduce) { .vi-blur-tip__bubble { transition: none; } }
      `}</style>
      {blurButton}
      <span className="vi-blur-tip__bubble" role="tooltip">{blurTooltipText}</span>
    </span>
  ) : null;

  // VI-370 / VI-820 (AC #8): icon-only Show/Hide Timer toggle, inline with the
  // recording controls. Only on the 1st try — hidden on the 2nd/3rd try per
  // question (remainingTries drops below the max after the first stop). The timer
  // visualization itself carries over via `showTimer`.
  const timerToggle = remainingTries === MAX_RECORDING_TRIES ? (
    <TimerToggle showTimer={showTimer} onToggle={toggleTimer} isMobile={isMobile} />
  ) : null;

  // ===========================================================================
  // VI-834 — Redesigned (flag-on) jobseeker video recording layout.
  // View layer only: reuses every ref/handler/id from the logic above. When the
  // flag is off, control falls through to the untouched legacy return below.
  // ===========================================================================
  if (newLayout) {
    const POPPINS = "'Poppins', sans-serif";
    const isRecordingPhase = isRecording || isCountingDown;

    const cameraPlayer = (
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '768 / 396',
          background: '#e3e2e4',
          borderRadius: '8px',
          border: '1.607px solid #adb5bd',
          overflow: 'hidden',
          boxShadow:
            '0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)',
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          width={640}
          height={480}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
            display: isCameraOn && !isReviewing ? 'block' : 'none',
            zIndex: showBlurCanvas ? 1 : 2,
          }}
        />
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: showBlurCanvas ? 2 : 1,
            visibility: showBlurCanvas && !isReviewing ? 'visible' : 'hidden',
          }}
        />
        {/* Cosmetic bottom gradient over the live feed (Figma Overlay Gradient) */}
        {isCameraOn && !isReviewing && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 50%)',
              pointerEvents: 'none',
              zIndex: 4,
            }}
          />
        )}
        {isCountingDown && <CountdownOverlay value={countdownValue} />}
        {showTimer && !isCountingDown && (isRecording || !hasRecorded) && (
          <AnswerTimerBadge recordingTime={recordingTime} isMobile={isMobile} isRecording={isRecording} />
        )}
        {!isCameraOn && !hasRecorded && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${onboardingData.assets.cameraPlaceholder})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}
        {isReviewing && (() => {
          const previewUrl =
            canChooseFinalResponse && takes.length > 0
              ? takes[selectedTakeIndex >= 0 ? selectedTakeIndex : takes.length - 1]?.url ?? recordedUrl
              : recordedUrl;
          return (
            <div style={{ position: 'absolute', inset: 0, zIndex: 3 }}>
              <VideoPlayback src={previewUrl} />
            </div>
          );
        })()}
      </div>
    );

    // VI-834: circular-arrows glyph reused by Re-Record (blue) + tries text (grey).
    const refreshIcon = (color: string, size = 16) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        <path d="M21 3v6h-6" />
      </svg>
    );

    // "N Tries Available" — plain grey text with the refresh glyph (Figma), next
    // to Re-Record in the review state.
    const triesText = (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontFamily: POPPINS,
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '20px',
          color: '#6c757d',
          whiteSpace: 'nowrap',
        }}
      >
        {refreshIcon('#6c757d', 14)}
        {remainingTries} {remainingTries === 1 ? 'Try' : 'Tries'} Available
      </span>
    );

    const startStopButton = !isRecordingPhase ? (
      // Figma: Start is an OUTLINE button — white bg, brand-blue border/text,
      // leading blue record dot (NOT a filled button).
      <button
        onClick={startCountdown}
        disabled={processingButtons.start}
        id="btn-startrecord-click"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '40px',
          padding: '0 20px',
          background: '#eaf3fc',
          color: '#2f86dd',
          border: '1px solid #2f86dd',
          borderRadius: '8px',
          fontFamily: POPPINS,
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          cursor: processingButtons.start ? 'wait' : 'pointer',
        }}
      >
        {isStartingRecording || (processingButtons.start && !isRecording) ? (
          <Spinner size="sm" color="primary" />
        ) : (
          <>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#2f86dd', flexShrink: 0 }} />
            Start Recording
          </>
        )}
      </button>
    ) : (
      <button
        onClick={() => {
          if (isCountingDown) {
            cancelCountdown();
          } else {
            handleButtonClick('stop', async () => { await setStopRecording(); });
          }
        }}
        disabled={processingButtons.stop && !isCountingDown}
        id="btn-stoprecord-click"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          height: '40px',
          padding: '0 24px',
          background: '#dc2626',
          color: '#ffffff',
          border: 'none',
          borderRadius: '4px',
          fontFamily: POPPINS,
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          cursor: 'pointer',
        }}
      >
        {processingButtons.stop && !isCountingDown ? (
          <Spinner size="sm" color="light" />
        ) : (
          <>
            <span style={{ width: '14px', height: '14px', background: '#ffffff', borderRadius: '2px', flexShrink: 0 }} />
            {isCountingDown ? 'Stop' : 'Stop Recording'}
          </>
        )}
      </button>
    );

    const assistanceText = (
      <p
        style={{
          margin: 0,
          fontFamily: POPPINS,
          fontSize: '12px',
          lineHeight: '18px',
          color: '#6c757d',
        }}
      >
        If you need any assistance with this process, please reach out to{' '}
        <a
          href="mailto:jobseeker-support@jobtarget.com"
          id="btn-jobseekersupport-click"
          style={{ color: '#2f86dd', textDecoration: 'none' }}
        >
          Job Seeker Support
        </a>
        .
      </p>
    );

    // Re-Record lives in the control row (Figma), matching Start/Stop height (40).
    // Outline + brand-blue text/glyph.
    const reRecordButton = (
      <button
        onClick={handleRerecordClick}
        disabled={remainingTries === 0 || processingButtons.reRecord || processingButtons.submit || isFinalizingTake}
        id="btn-rerecord-click"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '40px',
          padding: '0 20px',
          background: '#eaf3fc',
          color: '#2f86dd',
          border: '1px solid #2f86dd',
          borderRadius: '8px',
          fontFamily: POPPINS,
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
          opacity: remainingTries === 0 ? 0.5 : 1,
        }}
      >
        {isStartingRecording || (processingButtons.reRecord && !isRecording) ? (
          <Spinner size="sm" color="primary" />
        ) : (
          <>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#2f86dd', flexShrink: 0 }} />
            Re-Record
          </>
        )}
      </button>
    );

    const submitButton = (
      <button
        onClick={() => handleButtonClick('submit', async () => submitStreamRecording())}
        disabled={!canSubmitAnswer || processingButtons.submit}
        aria-busy={processingButtons.submit}
        id="submit-interview-answer-button"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '48px',
          padding: '0 32px',
          background: '#2f86dd',
          color: '#ffffff',
          border: 'none',
          borderRadius: '4px',
          fontFamily: POPPINS,
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          cursor: canSubmitAnswer && !processingButtons.submit ? 'pointer' : 'not-allowed',
          opacity: canSubmitAnswer ? 1 : 0.5,
          width: isMobile ? '100%' : 'auto',
        }}
      >
        {processingButtons.submit ? <Spinner size="sm" color="light" label="Submitting your answer" /> : (isLastQuestion ? 'Submit & Finish' : 'Submit & Continue')}
      </button>
    );

    const leftButton = isReviewing ? reRecordButton : startStopButton;

    return (
      // Figma: desktop canvas is grey (#e9ecef); mobile canvas is white.
      <div style={{ background: isMobile ? '#ffffff' : '#e9ecef', minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
        {toast && (
          <ToastMessage {...toast} isOpen={showToast} onClose={handleToastClose} />
        )}

        {isLoading ? (
          <div style={{ flex: '1 0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Spinner size={isMobile ? 'lg' : 'xl'} />
          </div>
        ) : (
          <>
            {/* Content is capped + centered; the canvas behind it stays full-bleed.
                On mobile it's a flex column so the question card can drop to the
                bottom (Figma bottom-sheet), just above the submit bar. */}
            <div
              style={{
                flex: '1 0 auto',
                width: '100%',
                maxWidth: '1240px',
                margin: '0 auto',
                boxSizing: 'border-box',
                // Mobile leaves room at the bottom for the fixed question sheet + submit bar.
                padding: isMobile ? '16px 16px 260px' : '32px 50px',
              }}
            >
              {/* Title block — Figma desktop gap is 0 (line-height only); mobile 8. */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? '8px' : '0px',
                  marginBottom: isMobile ? '24px' : '32px',
                }}
              >
                <h1
                  style={{
                    margin: 0,
                    fontFamily: POPPINS,
                    fontWeight: 600,
                    fontSize: isMobile ? '20px' : '23px',
                    lineHeight: isMobile ? '28px' : '32px',
                    color: '#020305',
                  }}
                >
                  {applicantFirstName ? `Hi ${applicantFirstName}! ` : ''}Welcome to your interview
                  {companyName ? <> with {companyName.toUpperCase()}</> : ''}
                </h1>
                {(jobTitle || jobLocation) && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: '8px',
                      fontFamily: POPPINS,
                      fontSize: '14px',
                      lineHeight: '26px',
                    }}
                  >
                    {jobTitle && <span style={{ color: '#272a2f' }}>{jobTitle}</span>}
                    {jobTitle && jobLocation && <span style={{ color: '#6c757d' }}>•</span>}
                    {/* Mobile stacks the location onto its own line (Figma). */}
                    {jobLocation && <span style={{ color: '#6c757d', flexBasis: isMobile ? '100%' : 'auto' }}>{jobLocation}</span>}
                  </div>
                )}
                {job && (
                  <button
                    onClick={() => setShowJobDetails(true)}
                    id="btn-showmore-click"
                    style={{
                      alignSelf: 'flex-start',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontFamily: POPPINS,
                      fontWeight: 500,
                      fontSize: '13px',
                      lineHeight: '20px',
                      color: '#0045a1',
                      textDecoration: 'underline',
                    }}
                  >
                    Show More
                  </button>
                )}
              </div>

              {/* Two columns */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '24px' : '40px',
                  alignItems: isMobile ? 'stretch' : 'flex-start',
                  justifyContent: isMobile ? 'flex-start' : 'center',
                }}
              >
                {/* Left — player + controls */}
                <div
                  style={{
                    flex: isMobile ? 'none' : '1 1 0',
                    width: isMobile ? '100%' : 'auto',
                    maxWidth: isMobile ? '100%' : '660px',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                  }}
                >
                  {cameraPlayer}

                  {/* Control row. Left button cycles Start → Stop → Re-Record;
                      timer + blur stay on the right. Desktop: one row (button +
                      tries left). Mobile (Figma): button + timer/blur on one row,
                      "N Tries Available" on its own line below. */}
                  {isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        {leftButton}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TimerToggle showTimer={showTimer} onToggle={toggleTimer} isMobile={isMobile} redesign />
                          {blurControl}
                        </div>
                      </div>
                      {!isRecordingPhase && triesText}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        {leftButton}
                        {!isRecordingPhase && triesText}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <TimerToggle showTimer={showTimer} onToggle={toggleTimer} isMobile={isMobile} redesign />
                        {blurControl}
                      </div>
                    </div>
                  )}

                  {canChooseFinalResponse && takes.length > 0 && (
                    <PickYourAnswer
                      takes={takes}
                      selectedIndex={selectedTakeIndex}
                      onSelect={handleTakeSelect}
                      isMobile={isMobile}
                    />
                  )}
                </div>

                {/* Right — question card (DESKTOP only; mobile uses the fixed
                    bottom sheet rendered below). */}
                {!isMobile && (
                <div
                  style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    padding: '24px 32px',
                    flex: '0 0 440px',
                    width: '440px',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    alignSelf: 'flex-start',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 8px',
                          background: '#f4f5f9',
                          border: '1px solid #e9ecef',
                          borderRadius: '4px',
                          fontFamily: POPPINS,
                          fontSize: '12px',
                          fontWeight: 500,
                          lineHeight: '16px',
                          letterSpacing: '0.24px',
                          color: '#585b62',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Question {currentQuestionIndex + 1} of {totalQuestions}
                      </span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontFamily: POPPINS,
                          fontSize: '12px',
                          fontWeight: 500,
                          lineHeight: '16px',
                          letterSpacing: '0.24px',
                          color: '#a4a7ac',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <i className="far fa-clock" style={{ fontSize: '16px' }} />
                        2 mins limit
                      </span>
                    </div>
                    <h2
                      style={{
                        margin: 0,
                        fontFamily: POPPINS,
                        fontWeight: 600,
                        fontSize: isMobile ? '16px' : '23px',
                        lineHeight: isMobile ? '24px' : '32px',
                        color: '#323235',
                      }}
                    >
                      {currentQuestion?.questionText}
                    </h2>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Desktop bottom bar — full-bleed white; capped inner content:
                assistance (left) + Submit & Continue (right). */}
            {!isMobile && (
            <div style={{ borderTop: '1px solid #d9dade', background: '#ffffff', width: '100%' }}>
              <div
                style={{
                  maxWidth: '1240px',
                  margin: '0 auto',
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '16px 50px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SubmitStatus status={submitStatus} startedAt={submitStartedAt} />
                  {assistanceText}
                </div>
                {submitButton}
              </div>
            </div>
            )}

            {/* Mobile: fixed bottom sheet — the question panel (collapsible,
                slides up over the controls on tap) sits above the submit bar. */}
            {isMobile && (
              <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 60, display: 'flex', flexDirection: 'column' }}>
                {/* Question sheet */}
                <div
                  onClick={() => { if (!questionExpanded) setQuestionExpanded(true); }}
                  style={{
                    background: '#ffffff',
                    borderTop: '1px solid #e9ecef',
                    borderLeft: '1px solid #e9ecef',
                    borderRight: '1px solid #e9ecef',
                    borderTopLeftRadius: '16px',
                    borderTopRightRadius: '16px',
                    boxShadow: '0 -8px 28px rgba(13,45,77,0.14), 0 -2px 4px rgba(173,181,189,0.25)',
                    padding: '12px 24px',
                    cursor: questionExpanded ? 'default' : 'pointer',
                    maxHeight: questionExpanded ? '55vh' : '88px',
                    overflowY: questionExpanded ? 'auto' : 'hidden',
                    transition: 'max-height 0.3s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                    <span
                      style={{
                        display: 'inline-flex', alignItems: 'center', padding: '4px 8px',
                        background: '#f4f5f9', border: '1px solid #e9ecef', borderRadius: '4px',
                        fontFamily: POPPINS, fontSize: '12px', fontWeight: 500, lineHeight: '16px',
                        letterSpacing: '0.24px', color: '#585b62', whiteSpace: 'nowrap',
                      }}
                    >
                      Question {currentQuestionIndex + 1} of {totalQuestions}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: POPPINS,
                          fontSize: '12px', fontWeight: 500, lineHeight: '16px', letterSpacing: '0.24px',
                          color: '#a4a7ac', whiteSpace: 'nowrap',
                        }}
                      >
                        <i className="far fa-clock" style={{ fontSize: '16px' }} />
                        2 mins limit
                      </span>
                      {questionExpanded && (
                        <button
                          type="button"
                          aria-label="Collapse question"
                          onClick={(e) => { e.stopPropagation(); setQuestionExpanded(false); }}
                          style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: '#585b62', display: 'inline-flex', lineHeight: 0 }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <h2
                    style={{
                      margin: 0,
                      fontFamily: POPPINS,
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: '#323235',
                      ...(questionExpanded
                        ? {}
                        : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                    }}
                  >
                    {currentQuestion?.questionText}
                  </h2>
                </div>
                {/* Submit + assistance — always visible at the very bottom */}
                <div style={{ background: '#ffffff', borderTop: '1px solid #d9dade', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {submitButton}
                  <SubmitStatus status={submitStatus} startedAt={submitStartedAt} />
                  {assistanceText}
                </div>
              </div>
            )}
          </>
        )}

        {job && (
          <JobDetailsModal
            isOpen={showJobDetails}
            onClose={() => setShowJobDetails(false)}
            job={job}
          />
        )}

        <ExitIntentModal
          isOpen={showExitIntentModal}
          title="Continue your interview now?"
          body="Your responses have been saved automatically. You can finish now or come back later."
          primaryLabel="Continue Interview"
          secondaryLabel="Remind me Later"
          primaryId="btn-exitintent-continue-interview-click"
          secondaryId="btn-exitintent-remind-me-later-click"
          onPrimary={handleExitIntentContinueInterview}
          onSecondary={handleExitIntentRemindMeLater}
        />

        {/* Hidden GTM permission/take click triggers (unchanged) */}
        <button id="btn-allow-click" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-block-click" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-askpermission-click" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-take1" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-take2" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-take3" style={{ display: 'none' }} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: isMobile ? '1rem' : '2rem',
        paddingTop: '0',
      }}
    >
      {toast && (
        <ToastMessage
          {...toast}
          isOpen={showToast}
          onClose={handleToastClose}
        />
      )}
      <div
        style={{
          width: isMobile ? '100%' : '70%',
          textAlign: 'left',
          padding: '0',
          borderRadius: '8px',
          marginTop: '0',
        }}
      >
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
          </div>
        ) : job ? (
          <JobCard job={job} isMobile={isMobile} />
        ) : (
          <div>No job information available</div>
        )}

        {/* Main content area - two columns on desktop, stacked on mobile */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '2rem',
              minHeight: 'calc(100vh - 400px)',
            }}
          >
            {/* Left Column - Question */}
            <div
              style={{
                flex: isMobile ? '1' : '1',
              }}
            >
              {/* Question Counter */}
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#555', fontWeight: '500' }}>
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </p>
              </div>

              {/* Question Text*/}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  marginBottom: isMobile ? '1.2rem' : '1.5rem',
                }}
              >
                <h2
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '500',
                    color: '#333',
                    margin: '0',
                  }}
                >
                  "{currentQuestion.questionText}"
                </h2>

              </div>
              <span style={{
                fontSize: '0.75rem',
                color: '#6c757d',
                margin: '0',
                marginLeft: 'auto',
                marginTop: '0.2rem',
              }}>
                Maximum Answer Length: 2 Minutes
              </span>

              {/* VI-368: Pick Your Answer lives in the left column, below the question */}
              {canChooseFinalResponse && takes.length > 0 && (
                <PickYourAnswer
                  takes={takes}
                  selectedIndex={selectedTakeIndex}
                  onSelect={handleTakeSelect}
                  isMobile={isMobile}
                />
              )}
            </div>

            {/* Right Column - Camera and Controls */}
            <div style={{ flex: isMobile ? '1' : '1' }}>

              {/* Camera Preview with Gradient Overlay */}
              <div
                style={{
                  width: '100%',
                  aspectRatio: '4/3',
                  position: 'relative',
                  borderRadius: '8px',
                  border: '1px solid #e0e4e8',
                  marginBottom: '1rem',
                  overflow: 'hidden',
                }}
              >
                {/* Always render video element but control visibility */}
                {/* <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: isCameraOn ? 'block' : 'none',
                }}
              /> */}

                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  width={640}
                  height={480}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    // Hide the live feed while reviewing a recorded/restored take.
                    display: isCameraOn && !isReviewing ? 'block' : 'none',
                    // Stay on top until the blur engine has produced a fresh frame.
                    // Avoids black/frozen flashes during enable + question transitions.
                    zIndex: showBlurCanvas ? 1 : 2,
                  }}
                />

                {/* Canvas for blur — only swapped on top once the engine renders a
                    fresh frame for this session (see showBlurCanvas).
                    Pixel buffer sized by useBackgroundBlur to match camera dims. */}
                <canvas
                  ref={canvasRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: showBlurCanvas ? 2 : 1,
                    visibility: showBlurCanvas && !isReviewing ? 'visible' : 'hidden',
                  }}
                />


                {/* VI-368: countdown overlay — shown over camera preview during 3-2-1 */}
                {isCountingDown && (
                  <CountdownOverlay value={countdownValue} />
                )}

                {/* VI-370: optional answer timer. Visible in the "upcoming
                    recording" state (static 02:00, so jobseekers can preview it)
                    and while recording (counting down). Hidden during the 3-2-1
                    countdown overlay and while reviewing a recorded take. */}
                {showTimer && !isCountingDown && (isRecording || !hasRecorded) && (
                  <AnswerTimerBadge recordingTime={recordingTime} isMobile={isMobile} isRecording={isRecording} />
                )}

                {/* Placeholder when nothing is recorded and the camera is off */}
                {!isCameraOn && !hasRecorded && (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${onboardingData.assets.cameraPlaceholder})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }} />
                )}
                {/* VI-368: selected take playback — shown whenever reviewing, even if
                    the camera was re-started on mount after a refresh. Rendered above
                    the (now hidden) live video. Falls back to latest recordedUrl for
                    the Control group or before any take is stored. */}
                {isReviewing && (() => {
                  const previewUrl =
                    canChooseFinalResponse && takes.length > 0
                      ? takes[selectedTakeIndex >= 0 ? selectedTakeIndex : takes.length - 1]?.url ?? recordedUrl
                      : recordedUrl;
                  return (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 3 }}>
                      <VideoPlayback src={previewUrl} />
                    </div>
                  );
                })()}
              </div>

            {/* Recording Controls */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                // VI-820: wrap instead of overflowing on narrow (Android) widths
                // so no control needs horizontal scrolling to reach.
                flexWrap: 'wrap',
                gap: isMobile ? '0.5rem' : '1rem',
                marginBottom: '1rem',
              }}
            >
              {/* Show different controls based on recording state */}
              {!isRecording && !isCountingDown && !hasRecorded ? (
                <>
                  <button
                    onClick={startCountdown}
                    disabled={processingButtons.start}
                    id="btn-startrecord-click"
                    style={{
                      // VI-702: match the Stop button's padding so the whole button set is consistent
                      padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 0.8rem',
                      backgroundColor: '#0d6efd',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    {isStartingRecording || (processingButtons.start && !isRecording) ? (
                      <>
                        <Spinner size="sm" color="light" />
                      </>
                    ) : (
                      <>
                        {isRecording ? (
                          <>
                            Stop Recording
                          </>
                        ) : (
                          <>
                            Start Recording
                          </>
                        )}
                      </>
                    )}
                  </button>

                    <div
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e9ecef',
                        borderRadius: '16px',
                        fontSize: '0.85rem',
                        color: '#6c757d',
                      }}
                    >
                      {remainingTries} tries left
                    </div>

                    {/* VI-370 + VI-702: icon toggles grouped on the right — only
                        rendered when at least one icon is present, so tries 2/3 or
                        blur-unsupported devices don't leave an empty flex slot. */}
                    {(timerToggle || blurButton) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {timerToggle}
                        {blurButton}
                      </div>
                    )}
                  </>
                ) : (isRecording || isCountingDown) ? (
                  <>
                    <button
                      onClick={() => {
                        if (isCountingDown) {
                          cancelCountdown();
                        } else {
                          handleButtonClick('stop', async () => { await setStopRecording(); });
                        }
                      }}
                      disabled={processingButtons.stop && !isCountingDown}
                      id="btn-stoprecord-click"
                      style={{
                        // VI-702: match Figma — narrower stop button
                        padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 0.8rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: '500',
                        cursor: 'pointer',
                      }}
                    >
                      {processingButtons.stop && !isCountingDown
                        ? <Spinner size="sm" color="light" />
                        : isCountingDown ? 'Stop' : 'Stop Recording'
                      }
                    </button>
                    {
                      // VI-370: suppress the legacy last-30s text when the optional
                      // timer is on, so we never show two competing readouts.
                      !showTimer && recordingTime >= 90 && (
                        <span
                          style={{
                            padding: '0.2rem 0.8rem',
                            borderRadius: '10px',
                            fontWeight: '500',
                            fontSize: '0.85rem',
                            color: '#6c757d',
                          }}
                        >
                          {formatTimeRemaining()}
                        </span>
                      )
                    }

                  <div
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#e9ecef',
                      borderRadius: '16px',
                      fontSize: '0.85rem',
                      color: '#6c757d',
                    }}
                    id="remaining-tries-left"
                  >
                    {remainingTries} tries left
                  </div>

                  {/* VI-370 + VI-702: icon toggles grouped on the right — only
                      rendered when at least one icon is present. */}
                  {(timerToggle || blurButton) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {timerToggle}
                      {blurButton}
                    </div>
                  )}
                </>
              )
              : /* Post-recording UI */
              (isMobile ? (
                /* Mobile layout - stacked */
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      gap: '0.75rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <button
                        onClick={handleRerecordClick}
                        disabled={remainingTries === 0 || processingButtons.submit || isFinalizingTake}
                        style={{
                          flex: '1',
                          marginRight: '0.5rem',
                          // VI-702: match the Stop button's padding
                          padding: '0.4rem 0.6rem',
                          backgroundColor: 'white',
                          color: '#333',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontWeight: '500',
                          cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
                          opacity: remainingTries === 0 ? 0.5 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          justifyContent: 'center',
                          height: '40px',
                        }}
                        id="btn-rerecord-click"
                      >
                        {isStartingRecording || (processingButtons.reRecord && !isRecording) ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                        <img src="/svgs/retry.svg" alt="Rerecord" />
                        Rerecord
                          </>
                        )}
                      </button>

                          <button
                            disabled={!canSubmitAnswer || processingButtons.submit}
                            aria-busy={processingButtons.submit}
                            onClick={() => handleButtonClick('submit', async () => submitStreamRecording())}
                            style={{
                              flex: '1',
                              marginLeft: '0.5rem',
                              // VI-702: match the Stop button's padding
                              padding: '0.4rem 0.6rem',
                              backgroundColor: '#11807a',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              gap: '0.5rem',
                              fontWeight: '500',
                              cursor: canSubmitAnswer ? 'pointer' : 'not-allowed',
                              opacity: canSubmitAnswer ? 1 : 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '40px',
                            }}
                            id="submit-interview-answer-button"
                          >
                            {processingButtons.submit ? <Spinner size="sm" color="light" label="Submitting your answer" /> : <><img src="/svgs/paperplane.svg" alt="Submit" /> Submit</>}
                          </button>
                        </div>

                    <div
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e9ecef',
                        borderRadius: '16px',
                        fontSize: '0.85rem',
                        color: '#6c757d',
                        textAlign: 'center',
                        width: 'fit-content',
                        margin: '0 auto',
                      }}
                    >
                      {remainingTries} tries left
                    </div>
                  </div>
                </>
              ) : (
                /* Desktop layout - side by side with tries left in middle */
                <>
                  <div
                    style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%' }}
                  >
                    <button
                      onClick={handleRerecordClick}
                      disabled={processingButtons.reRecord || processingButtons.submit || isFinalizingTake}
                      id="btn-rerecord-click"
                      style={{
                        // VI-702: match the Stop button's padding; content-sized (no fixed
                        // width/height) so the padding actually governs the button size
                        padding: '0.5rem 0.8rem',
                        backgroundColor: 'white',
                        color: '#333',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontWeight: '500',
                        cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
                        opacity: remainingTries === 0 ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        justifyContent: 'center',
                      }}
                    >
                      {isStartingRecording || (processingButtons.reRecord && !isRecording) ? <Spinner size="sm" /> : <><img src="/svgs/retry.svg" alt="Rerecord" />
                        Rerecord</>}
                    </button>

                        <div
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#e9ecef',
                            borderRadius: '16px',
                            fontSize: '0.85rem',
                            color: '#6c757d',
                            margin: '0 auto',
                          }}
                        >
                          {remainingTries} tries left
                        </div>

                        <button
                          onClick={() => handleButtonClick('submit', async () => submitStreamRecording())}
                          disabled={!canSubmitAnswer || processingButtons.submit}
                          aria-busy={processingButtons.submit}
                          style={{
                            // VI-702: match the Stop button's padding; content-sized like Rerecord
                            padding: '0.5rem 0.8rem',
                            backgroundColor: '#11807a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontWeight: '500',
                            cursor: canSubmitAnswer ? 'pointer' : 'not-allowed',
                            opacity: canSubmitAnswer ? 1 : 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            justifyContent: 'center',
                          }}
                        >
                          {processingButtons.submit ? <Spinner size="sm" color="light" label="Submitting your answer" /> : <><img src="/svgs/paperplane.svg" alt="Submit" /> Submit</>}
                        </button>
                      </div>
                    </>
                  ))}
              </div>

              {/* VI-900: submit progress (legacy layout, mobile + desktop) */}
              <SubmitStatus status={submitStatus} startedAt={submitStartedAt} align="center" marginTop="1rem" />

              {/* Support Message */}
              <div style={{
                textAlign: 'center',
                marginBottom: '1rem',
                marginTop: isMobile ? '1.5rem' : '1.8rem',
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                color: '#6c757d',
              }}>
                Need Assistance? Please email us at <a href="mailto:jobseeker-support@jobtarget.com" id="btn-jobseekersupport-click" style={{ color: '#6c757d', textDecoration: 'underline' }}>Job Seeker Support</a>.
              </div>
            </div>
          </div>)}
        {/* Hidden elements for GTM permission click triggers */}
        <button id="btn-allow-click" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-block-click" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-askpermission-click" style={{ display: 'none' }} aria-hidden="true" />
        {/* VI-368: hidden GTM click triggers for take selection events */}
        <button id="btn-take1" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-take2" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-take3" style={{ display: 'none' }} aria-hidden="true" />
      </div>

      <ExitIntentModal
        isOpen={showExitIntentModal}
        title="Continue your interview now?"
        body="Your responses have been saved automatically. You can finish now or come back later."
        primaryLabel="Continue Interview"
        secondaryLabel="Remind me Later"
        primaryId="btn-exitintent-continue-interview-click"
        secondaryId="btn-exitintent-remind-me-later-click"
        onPrimary={handleExitIntentContinueInterview}
        onSecondary={handleExitIntentRemindMeLater}
      />
    </div>
  );
};

export default Interview;
